// packages/console/src/lib/bitrate.ts
//
// Rolling live-bitrate estimate for the HLS player overlay. hls.js hands each
// buffered fragment its byte size and duration; averaging the last few segments
// gives the ACTUAL delivered bitrate (the real VBR figure), not the declared
// EXT-X-STREAM-INF cap. A short window keeps the readout responsive to scene
// changes (motion pushes bitrate up) while staying steady enough to read.

export interface BitrateSample {
  // bytes is the fragment's transferred size; seconds is its media duration.
  bytes: number;
  seconds: number;
}

// BITRATE_WINDOW is how many recent segments the average spans (~16s at 4s
// segments): long enough to be steady, short enough to track scene changes.
export const BITRATE_WINDOW = 4;

// rollingKbps averages the last BITRATE_WINDOW samples into kilobits per second,
// or null when there is not yet any valid duration to divide by.
export function rollingKbps(samples: BitrateSample[]): number | null {
  const w = samples.slice(-BITRATE_WINDOW);
  const seconds = w.reduce((a, s) => a + s.seconds, 0);
  if (seconds <= 0) return null;
  const bytes = w.reduce((a, s) => a + s.bytes, 0);
  return Math.round((bytes * 8) / seconds / 1000);
}

// fmtKbps renders a kbps value for the overlay: kbps under 1000, else Mbps with
// one decimal. The tilde marks it as a rolling estimate.
export function fmtKbps(kbps: number | null): string {
  if (kbps == null) return "~ … kbps";
  if (kbps < 1000) return `~ ${kbps} kbps`;
  return `~ ${(kbps / 1000).toFixed(1)} Mbps`;
}
